/**
 * @typedef {import('./schema').UserProperties} UserProperties
 */
import * as constants from '@redbubble/design-system/react/constants';
import { format, parse, isValid } from 'date-fns';
import { round } from 'lodash';
import GaUserTypes from '../analytics/gaUserTypes';
import { serializeExperiments } from '../experiment';
import {
  AnalyticsLocalStorageKeys,
  AnalyticsItemsListIDNameMap,
  AnalyticsContextTypes
} from './constants';
import { storage } from '../persistentStorage';
import config from '../../../config';
import { clientError } from '../errorReporting';
/**
 *
 * @param {string} date
 * @returns {string}
 */
export const formatDate = date => {
  const parsedDate = parse(date);
  if (date && isValid(parsedDate)) {
    return format(parsedDate, 'YYYYMMDD');
  }
  return date;
};

// Cannot use dot notation, would need to allow dangling underscore
// eslint-disable-next-line dot-notation
const gaId = cookies => (cookies.get('_ga') ? cookies.get('_ga') : '');

/**
 * @typedef {Object} buildUserPropertiesArgs
 * @property {Object} userInfo
 * @property {Object} userAccount
 * @property {Object} cookies
 * @property {String} rbVisitorID
 * @returns {UserProperties}
 */
export const buildUserProperties = ({
  userInfo,
  userAccount,
  cookies,
  rbVisitorID
}) => {
  return {
    event: 'user_properties',
    custom_client_id: gaId(cookies), // should we do an eary return if there is no GA client id? this will be missing for initial page_view event
    experiment_enrolments: serializeExperiments(userInfo.enrolments) || 'none',
    federated_id: userInfo.federatedId,
    joined_date:
      userAccount && userAccount.joinedAt
        ? formatDate(userAccount.joinedAt)
        : '',
    locale: userInfo.locale,
    onboarded_date:
      userAccount && userAccount.onboardedAt
        ? formatDate(userAccount.onboardedAt)
        : '',
    user_id: userInfo.id,
    user_type: GaUserTypes.getVisitorType(cookies, userInfo),
    visitor_id_cookie: rbVisitorID,
    custom_country_code: userInfo.country
  };
};

export function getHostName() {
  const { hostname } = new URL(config('rbmBaseWebUrl'));
  return hostname;
}

export const AnalyticsStorage = {
  isValidKey(key) {
    return Object.values(AnalyticsLocalStorageKeys).includes(key);
  },

  getValue(key) {
    if (!this.isValidKey(key)) {
      clientError(
        `Unable to get analytics local storage value, invalid key provided: "${key}"`
      );
      return null;
    }

    try {
      return storage.getItem(key);
    } catch (error) {
      clientError(`Error getting localStorage for key "${key}":`, error);
      return null;
    }
  },

  setValue(key, value) {
    if (!this.isValidKey(key)) {
      clientError(
        `Unable to set analytics local storage value, invalid key provided: "${key}"`
      );
      return;
    }

    try {
      storage.setItem(key, value);
    } catch (error) {
      clientError(
        `Error setting value for key "${key}" in localStorage:`,
        error
      );
    }
  }
};

const getDiscountValue = price => {
  if (price && price.discount && price.discount.percent > 0) {
    return round(price.amount - price.discount.amount, 2);
  }
  return 0;
};

export const parseInventoryItemToAnalyticsItem = ({
  inventoryItem,
  index,
  analyticsComponentID
}) => {
  if (!inventoryItem) return null;

  return {
    discount: getDiscountValue(inventoryItem.price),
    index: Number.isInteger(index) ? index : null,
    work_id: inventoryItem.workId,
    product_type_id: inventoryItem.productTypeId,
    blank_item_id: inventoryItem.blankItemId,
    inventory_item_id: inventoryItem.id,
    retargeting_id: inventoryItem.marketingProductTypeId,
    item_id: inventoryItem.id,
    price: inventoryItem.price ? inventoryItem.price.amount : null,
    quantity: 1,
    item_list_id: analyticsComponentID,
    item_list_name: AnalyticsItemsListIDNameMap[analyticsComponentID]
  };
};

export const parseWorkToAnalyticsItem = ({
  work,
  index,
  analyticsComponentID
}) => {
  if (!work) return null;

  return {
    discount: 0, // does not exist on work object
    index: Number.isInteger(index) ? index : null,
    work_id: work.workId,
    item_id: work.workId,
    item_list_id: analyticsComponentID,
    item_list_name: AnalyticsItemsListIDNameMap[analyticsComponentID],
    price: work.price ? work.price.amount : null, // does not exist on work object
    quantity: 1
  };
};

export const getItemValue = item => {
  if (item.price) {
    if (item.price.discount && item.price.discount.percent > 0)
      return String(item.price.discount.amount);

    return String(item.price.amount);
  }
  return '';
};

export const onSearchCallback = () => {
  AnalyticsStorage.setValue(
    AnalyticsLocalStorageKeys.RefContextType,
    AnalyticsContextTypes.SearchBox
  );
};

export const onTypaheadResultClick = type => {
  switch (type) {
    case constants.TRENDING:
      AnalyticsStorage.setValue(
        AnalyticsLocalStorageKeys.RefContextType,
        AnalyticsContextTypes.TrendingTypeahead
      );
      break;
    case constants.RECENT:
      AnalyticsStorage.setValue(
        AnalyticsLocalStorageKeys.RefContextType,
        AnalyticsContextTypes.RecentTypeahead
      );
      break;
    case constants.POPULAR:
      AnalyticsStorage.setValue(
        AnalyticsLocalStorageKeys.RefContextType,
        AnalyticsContextTypes.PopularTypeahead
      );
      break;
    case constants.ARTISTS:
      AnalyticsStorage.setValue(
        AnalyticsLocalStorageKeys.RefContextType,
        AnalyticsContextTypes.ArtistsTypeahead
      );
      break;
    case constants.CATEGORIES:
      AnalyticsStorage.setValue(
        AnalyticsLocalStorageKeys.RefContextType,
        AnalyticsContextTypes.CategoriesTypeahead
      );
      break;
    case constants.FALLBACK:
      AnalyticsStorage.setValue(
        AnalyticsLocalStorageKeys.RefContextType,
        AnalyticsContextTypes.FallbackTypeahead
      );
      break;
    default:
      break;
  }
};
